import { t } from '../../common/i18n/index.js';
import { getProductOfferingPriceDataFromType } from '../../common/utils/productOfferingsUtils.js';
import { PriceType } from '../HorizontalSubscriptionCard/types';
import { sortInstallmentOptionsForDisplay, sortInstallmentTermsForDisplay } from './DeviceDetailsPaymentCoCo.js';
import type { AddonProductOfferingWithSelection } from '../../common/types/productOfferings.js';
import type { ProductOfferingCartItems } from '../../common/types/shoppingBasketCoCo.js';
import type { InstallmentOption } from '../../generated/api/installmentOption.js';
import type { InstallmentTerm } from '../../generated/api/installmentTerm.js';
import type { ProductOfferingCoco } from '../../generated/api/productOfferingCoco';
import type { ProductOfferingPrice } from '../../generated/api/productOfferingPrice.js';
import type { ProductOfferingsResponse } from '../../generated/api/productOfferingsResponse.js';

export type DeviceChargeInput = {
  installmentOptions: InstallmentOption[];
  selectedInstallmentId: string;
  eppInstallmentTerms?: InstallmentTerm[];
  selectedEppTermId?: string;
  oneTimePrice?: number;
  selectedQuantity: number;
  selectedAddonRecurringTotal: number;
  selectedAddonOneTimePrice: number;
};

export type DeviceCharges = {
  selectedInstallmentOption?: InstallmentOption;
  selectedEppInstallmentTerm?: InstallmentTerm;
  numberOfInstallment: number;
  isOneTimePaymentSelected: boolean;
  monthlyCharge?: number;
  oneTimeCharge?: number;
};

type DeviceOfferWithCommercialAddons = ProductOfferingsResponse & {
  commercialAddonProductOffering?: ProductOfferingCoco[];
};

type PricingAwareCommercialAddon = {
  isChecked: boolean;
  quantity: number;
  isEppAddon: boolean;
};

type DeviceOfferWithVariants = ProductOfferingsResponse & {
  productOfferings?: ProductOfferingsResponse[];
  colorOption?: Array<{ offerId?: string }>;
};

export const generateCartItemId = (): string => `cartItem-${crypto.randomUUID()}`;

const mapInstallmentTermToOption = (term: InstallmentTerm): InstallmentOption => ({
  id: term.id,
  numberOfInstallment: term.duration,
  priceWithoutTax: term.priceWithoutTax,
  priceWithTax: term.priceWithTax,
  isDefault: term.isDefault,
});

export const getDeviceInstallmentOptions = (offer: ProductOfferingsResponse): InstallmentOption[] => {
  const installmentPriceData = getProductOfferingPriceDataFromType(
    offer?.productOfferingPrice,
    PriceType.ONE_TIME_CHARGE
  );
  const installmentOptions = installmentPriceData?.installmentOption?.length
    ? installmentPriceData.installmentOption
    : (installmentPriceData?.installmentTerm?.map(mapInstallmentTermToOption) ?? []);

  return sortInstallmentOptionsForDisplay(installmentOptions);
};

export const getEppDeviceInstallmentTerms = (offer: ProductOfferingCoco): InstallmentTerm[] => {
  const installmentTerms = offer?.productOfferingPrice?.flatMap(price => price.installmentTerm ?? []) ?? [];
  return sortInstallmentTermsForDisplay(installmentTerms);
};

export const isEppPricingActive = Boolean;

export const isAddonSelectableForPricing = (
  addon: Pick<PricingAwareCommercialAddon, 'isEppAddon'>,
  eppPricingSelected: boolean
): boolean => addon.isEppAddon === eppPricingSelected;

export const normalizeAddonsForPricing = <T extends PricingAwareCommercialAddon>(
  addons: T[],
  eppPricingSelected: boolean
): T[] =>
  addons.map(addon =>
    isAddonSelectableForPricing(addon, eppPricingSelected) ? addon : { ...addon, isChecked: false, quantity: 1 }
  );

export const getSelectedDeviceProductOffering = (
  offer: DeviceOfferWithVariants,
  selectedColorIndex = 0
): ProductOfferingsResponse | undefined => {
  const selectedOfferId = offer.colorOption?.[selectedColorIndex]?.offerId;

  if (!offer.productOfferings?.length) {
    return offer;
  }
  if (!selectedOfferId) {
    return offer.productOfferings[0];
  }

  return (
    offer.productOfferings.find(productOffering => productOffering.id === selectedOfferId) ?? offer.productOfferings[0]
  );
};

export const getCommercialAddonOfferings = (
  offer: DeviceOfferWithCommercialAddons,
  selectedProductOffering?: DeviceOfferWithCommercialAddons
): ProductOfferingCoco[] => {
  const selectedAddons = selectedProductOffering?.commercialAddonProductOffering;
  const deviceLevelAddons = offer.commercialAddonProductOffering;

  return selectedAddons ?? deviceLevelAddons ?? [];
};

export const calculateDeviceDetailCharges = ({
  installmentOptions,
  selectedInstallmentId,
  eppInstallmentTerms,
  selectedEppTermId,
  oneTimePrice,
  selectedQuantity,
  selectedAddonRecurringTotal,
  selectedAddonOneTimePrice,
}: DeviceChargeInput): DeviceCharges => {
  const quantity = Math.max(1, selectedQuantity);
  const addonRecurringTotal = selectedAddonRecurringTotal;
  const addonOneTimeTotal = selectedAddonOneTimePrice * quantity;

  if (selectedEppTermId) {
    const selectedEppInstallmentTerm = eppInstallmentTerms?.find(
      term => (term.id ?? String(term.duration)) === selectedEppTermId
    );
    const eppPrice = selectedEppInstallmentTerm?.priceWithoutTax ?? 0;
    const monthlyChargeInTotal = eppPrice * quantity + addonRecurringTotal;

    return {
      selectedEppInstallmentTerm,
      numberOfInstallment: selectedEppInstallmentTerm?.duration ?? 0,
      isOneTimePaymentSelected: false,
      monthlyCharge: monthlyChargeInTotal > 0 ? monthlyChargeInTotal : undefined,
      oneTimeCharge: addonOneTimeTotal > 0 ? addonOneTimeTotal : undefined,
    };
  }

  const selectedInstallmentOption = installmentOptions.find(
    option => String(option.numberOfInstallment) === selectedInstallmentId
  );

  const numberOfInstallment = selectedInstallmentOption?.numberOfInstallment ?? 0;
  const selectedInstallmentPrice = selectedInstallmentOption?.priceWithoutTax ?? 0;

  const isOneTimePaymentSelected = numberOfInstallment <= 1;
  const hasRecurringDevicePrice = numberOfInstallment > 1 && selectedInstallmentPrice > 0;

  const recurringDeviceTotal = hasRecurringDevicePrice ? selectedInstallmentPrice * quantity : 0;
  const deviceOneTimeTotal = isOneTimePaymentSelected ? (oneTimePrice ?? 0) * quantity : 0;

  const monthlyChargeTotal = recurringDeviceTotal + addonRecurringTotal;
  const oneTimeChargeTotal = deviceOneTimeTotal + addonOneTimeTotal;

  return {
    selectedInstallmentOption,
    numberOfInstallment,
    isOneTimePaymentSelected,
    monthlyCharge:
      !isOneTimePaymentSelected && (hasRecurringDevicePrice || addonRecurringTotal > 0)
        ? monthlyChargeTotal
        : undefined,
    oneTimeCharge: isOneTimePaymentSelected || addonOneTimeTotal > 0 ? oneTimeChargeTotal : undefined,
  };
};

export const buildSelectedDevicePrice = (
  basePrices: ProductOfferingPrice[],
  selectedInstallmentOption: InstallmentOption | undefined,
  numberOfInstallment: number,
  selectedInstallmentTerm?: InstallmentTerm
): ProductOfferingPrice[] => {
  const oneTimePrice = basePrices.find(priceEntry => priceEntry.priceType === PriceType.ONE_TIME_CHARGE);

  if (selectedInstallmentTerm) {
    return [
      {
        ...(oneTimePrice ?? {}),
        priceType: PriceType.RECURRING_CHARGE,
        priceWithoutTax: selectedInstallmentTerm.priceWithoutTax,
        priceWithTax: selectedInstallmentTerm.priceWithTax,
        installmentOption: undefined,
        installmentTerm: [selectedInstallmentTerm],
      },
    ];
  }

  if (selectedInstallmentOption && numberOfInstallment > 1) {
    return [
      {
        ...(oneTimePrice ?? {}),
        priceType: oneTimePrice?.priceType ?? PriceType.ONE_TIME_CHARGE,
        priceWithoutTax: selectedInstallmentOption.priceWithoutTax,
        priceWithTax: selectedInstallmentOption.priceWithTax,
        installmentOption: [selectedInstallmentOption],
      },
    ];
  }

  if (selectedInstallmentOption && oneTimePrice) {
    return [{ ...oneTimePrice, installmentOption: [selectedInstallmentOption] }];
  }
  return oneTimePrice ? [oneTimePrice] : basePrices;
};

export const createDeviceCartItem = ({
  productOffering,
  quantity,
  commercialAddonProductOffering,
  selectedInstallmentOption,
  numberOfInstallment,
  selectedInstallmentTerm,
  imageUrl,
}: {
  productOffering: ProductOfferingsResponse;
  quantity: number;
  commercialAddonProductOffering: AddonProductOfferingWithSelection[];
  selectedInstallmentOption?: InstallmentOption;
  numberOfInstallment: number;
  selectedInstallmentTerm?: InstallmentTerm;
  imageUrl?: string;
}): ProductOfferingCartItems => {
  const baseOffer: ProductOfferingsResponse =
    selectedInstallmentTerm && productOffering.relatedEPPOffer
      ? { ...productOffering, ...productOffering.relatedEPPOffer }
      : productOffering;

  return {
    id: baseOffer.id,
    name: baseOffer.name,
    description: baseOffer.description,
    cartItemId: generateCartItemId(),
    quantity,
    productOfferingType: baseOffer.productOfferingType,
    productSpecification: baseOffer.productSpecification,
    productStock: baseOffer.productStock,
    productOfferingPrice: buildSelectedDevicePrice(
      baseOffer.productOfferingPrice ?? [],
      selectedInstallmentOption,
      numberOfInstallment,
      selectedInstallmentTerm
    ),
    imageUrl,
    commercialAddonProductOffering,
  };
};

export type ProductAvailabilityColor = 'red' | 'orange' | 'green';

export interface ProductAvailability {
  text: string;
  color?: ProductAvailabilityColor;
  isBackordered: boolean;
}

const AVAILABILITY_COLORS: ProductAvailabilityColor[] = ['red', 'orange', 'green'];

export type OfferingWithStock = Pick<ProductOfferingCoco, 'productSpecification' | 'productStock'>;

const getProductSpecificationValue = (offer: OfferingWithStock, specificationName: string): string | undefined =>
  offer.productSpecification?.find(
    specification => specification.name?.toLowerCase() === specificationName.toLowerCase()
  )?.value?.[0]?.value;

export const getProductStockLevel = (offer: OfferingWithStock): number | undefined => {
  const stocks = offer.productStock;
  if (!stocks?.length) {
    return undefined;
  }
  return stocks.reduce((total, stock) => total + Math.max(0, stock.stockLevel ?? 0), 0);
};

export const hasNoMoreShipmentsArriving = (offer: OfferingWithStock): boolean =>
  getProductSpecificationValue(offer, 'sapNoMoreShippingArriving')?.toLowerCase() === 'true';

/**
 * Maps a stock level to the same buckets the legacy device details page uses for its
 * availability ranges: 0, 1-10, 11-24 and 25+.
 */
const getStockLevelIndex = (stockLevel: number): number => {
  if (stockLevel >= 25) {
    return 3;
  } else if (stockLevel >= 11) {
    return 2;
  } else if (stockLevel >= 1) {
    return 1;
  }
  return 0;
};

export const getProductAvailability = (offer?: OfferingWithStock): ProductAvailability | null => {
  if (!offer) {
    return null;
  }

  const stockLevel = getProductStockLevel(offer);
  if (stockLevel === undefined) {
    return { text: t.BC0X('No availability information'), isBackordered: false };
  }

  const index = getStockLevelIndex(stockLevel);
  // Texts are indexed by the stock level bucket. `V2YZ` and `MADI` share the same English source
  // string but are separate translation keys on purpose: in Finnish/Swedish 11-24 units reads as
  // "Tuotetta varastossa" while 25+ reads as "Reilusti verkkokaupan varastossa". Do not merge them.
  const texts = hasNoMoreShipmentsArriving(offer)
    ? [
        t.T3CO('Out of Stock'),
        t.PLPS('Last ones in the stock, act fast'),
        t.L80Q('Restricted availability - act fast'),
        t.MADI('In Stock'),
      ]
    : [
        t.HGEL('Temporarily out of stock, will be delivered by backorder'),
        t.UJRH('Order now, restricted availability'),
        t.V2YZ('In Stock'),
        t.MADI('In Stock'),
      ];

  return {
    text: texts[index],
    color: AVAILABILITY_COLORS[Math.min(index, AVAILABILITY_COLORS.length - 1)],
    isBackordered: index === 0,
  };
};

export const toKauppaSlug = (value: unknown): string => {
  let raw = '';

  if (typeof value === 'string') {
    raw = value;
  } else if (typeof value === 'number') {
    raw = String(value);
  }

  if (!raw) {
    return '';
  }
  return raw
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
};
